import React, { useState, useEffect } from 'react';
import { diaryService } from '../services/diaryService';
import { PiCalendarPlus, PiTrashSimple, PiPencilLine, PiCheck, PiEye } from 'react-icons/pi';
import MDEditor from '@uiw/react-md-editor';

function Diary() {
  const [entries, setEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date().toISOString().split('T')[0]);
  const [currentMood, setCurrentMood] = useState('neutral');

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const response = await diaryService.getAllEntries();
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching diary entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntry = async () => {
    try {
      const response = await diaryService.createEntry({
        content: editedContent,
        mood: currentMood,
        date: currentDate
      });
      setEntries([...entries, response.data]);
      setSelectedEntry(response.data);
      setIsCreating(false);
      setIsEditing(false);
    } catch (error) {
      console.error('Error creating entry:', error);
    }
  };

  const handleUpdateEntry = async () => {
    try {
      const response = await diaryService.updateEntry(selectedEntry._id, {
        ...selectedEntry,
        content: editedContent
      });
      setEntries(entries.map(entry => 
        entry._id === selectedEntry._id ? response.data : entry
      ));
      setSelectedEntry(response.data);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating entry:', error);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }
    
    try {
      await diaryService.deleteEntry(entryId);
      setEntries(entries.filter(entry => entry._id !== entryId));
      if (selectedEntry?._id === entryId) {
        setSelectedEntry(null);
        setEditedContent('');
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
    }
  };

  const startNewEntry = () => {
    setSelectedEntry(null);
    setIsCreating(true);
    setEditedContent('');
    setCurrentDate(new Date().toISOString().split('T')[0]);
    setCurrentMood('neutral');
  };

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Left Panel - Entries List */}
      <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col md:w-72 lg:w-80">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <button
            onClick={startNewEntry}
            className="w-10 h-10 flex items-center justify-center bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors mx-auto"
          >
            <PiCalendarPlus className="w-5 h-5" />
          </button>
        </div>

        {/* Entries List with Loading State */}
        <div className="overflow-y-auto flex-1">
          {loading ? (
            Array(5).fill().map((_, index) => (
              <div key={index} className="p-3 border-b border-gray-200">
                <div className="h-6 bg-gray-200 rounded animate-pulse mb-2"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse w-2/3"></div>
              </div>
            ))
          ) : (
            entries.map(entry => (
              <div
                key={entry._id}
                className="group border-b border-gray-200 p-3 cursor-pointer hover:bg-gray-100"
                onClick={() => {
                  setSelectedEntry(entry);
                  setEditedContent(entry.content);
                  setIsEditing(false);
                }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium">
                    {new Date(entry.date).toLocaleDateString()}
                  </span>
                  <span className="text-xl">
                    {entry.mood === 'happy' ? '😊' : entry.mood === 'sad' ? '😢' : '😐'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 truncate">
                  {entry.content.replace(/[#*`]/g, '')}
                </p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Panel - Entry Editor */}
      <div className="flex-1 flex flex-col bg-white relative">
        {selectedEntry || isCreating ? (
          <>
            <div className="border-b border-gray-200 p-4 flex items-center justify-between bg-white sticky top-0 z-10">
              <div className="flex items-center gap-4">
                <input
                  type="date"
                  value={isCreating ? currentDate : selectedEntry?.date}
                  onChange={(e) => isCreating && setCurrentDate(e.target.value)}
                  disabled={!isCreating}
                  className="border rounded-md p-2"
                />
                <select
                  value={isCreating ? currentMood : selectedEntry?.mood}
                  onChange={(e) => isCreating && setCurrentMood(e.target.value)}
                  disabled={!isCreating}
                  className="border rounded-md p-2"
                >
                  <option value="happy">😊 Happy</option>
                  <option value="neutral">😐 Neutral</option>
                  <option value="sad">😢 Sad</option>
                </select>
              </div>
              <div className="flex gap-2">
                {(isEditing || isCreating) ? (
                  <>
                    <button
                      onClick={isCreating ? handleCreateEntry : handleUpdateEntry}
                      className="px-3 py-1.5 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg flex items-center gap-1"
                    >
                      <PiCheck className="w-4 h-4" />
                      {isCreating ? 'Create' : 'Save'}
                    </button>
                    {!isCreating && (
                      <button
                        onClick={() => setIsEditing(false)}
                        className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg flex items-center gap-1"
                      >
                        <PiEye className="w-4 h-4" />
                        Preview
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1"
                    >
                      <PiPencilLine className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteEntry(selectedEntry._id)}
                      className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1"
                    >
                      <PiTrashSimple className="w-4 h-4" />
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              {(isEditing || isCreating) ? (
                <div className="h-full">
                  <MDEditor
                    value={editedContent}
                    onChange={setEditedContent}
                    height="100%"
                    preview="edit"
                    className="wmde-markdown-var h-full"
                    previewOptions={{
                      style: {
                        backgroundColor: 'white',
                        color: '#374151'
                      }
                    }}
                    textareaProps={{
                      style: {
                        color: '#374151',
                        backgroundColor: 'white'
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="prose max-w-none p-8 h-full overflow-auto">
                  <MDEditor.Markdown
                    source={selectedEntry.content}
                    style={{
                      backgroundColor: 'white',
                      color: '#374151',
                      fontSize: '1rem',
                      lineHeight: '1.75'
                    }}
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <p className="mb-2">Select an entry to view or edit</p>
              <p className="text-sm">Or create a new entry using the + button</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Diary;
